import { useEffect, useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { fetchProfile, updateProfile } from '../api';

// Member self-service contact preferences.
//   - Phone number (stored normalized to E.164 server-side)
//   - Email opt-in (announcements/broadcasts by email)
//   - SMS opt-in (text broadcasts) — requires a phone on file
//
// The server normalizes/validates the phone and enforces the "phone required
// before SMS opt-in" rule; this UI mirrors those rules for fast feedback.

export default function ContactPreferences() {
  const { instance } = useMsal();
  const account = instance.getActiveAccount();
  const accountKey = account?.homeAccountId || account?.username || null;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState(null);
  const [phone, setPhone] = useState('');
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [smsOptIn, setSmsOptIn] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { ok, data } = await fetchProfile(instance, account);
      if (ok && data?.profile) {
        setPhone(data.profile.phone_e164 || '');
        setEmailOptIn(!!data.profile.email_opt_in);
        setSmsOptIn(!!data.profile.sms_opt_in);
      }
    } catch { /* non-fatal — show empty form */ }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountKey]);

  useEffect(() => { load(); }, [load]);

  const hasPhone = phone.trim().length > 0;
  // Can't opt into SMS without a phone number.
  const smsDisabled = !hasPhone;

  async function handleSave() {
    setSaving(true);
    setBanner(null);
    // Guard client-side too: SMS opt-in needs a phone.
    if (smsOptIn && !hasPhone) {
      setBanner({ kind: 'warn', text: 'Add a phone number before enabling SMS.' });
      setSaving(false);
      return;
    }
    try {
      const patch = {
        phone: phone.trim(),
        emailOptIn,
        smsOptIn: hasPhone ? smsOptIn : false,
      };
      const { ok, data, status } = await updateProfile(instance, account, patch);
      if (ok && data?.profile) {
        setPhone(data.profile.phone_e164 || '');
        setEmailOptIn(!!data.profile.email_opt_in);
        setSmsOptIn(!!data.profile.sms_opt_in);
        setBanner({ kind: 'ok', text: 'Contact preferences saved.' });
      } else {
        setBanner({ kind: 'warn', text: (data && (data.message || data.error)) || `HTTP ${status}` });
      }
    } catch (e) {
      setBanner({ kind: 'warn', text: e.message });
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="card">Loading contact preferences…</div>;

  return (
    <div className="card">
      <h2>Contact Preferences</h2>
      <p className="muted">Choose how Eagle Ridge Outdoor Club can reach you. You can opt out any time (or reply STOP to any text).</p>

      {banner && <div className={`notice ${banner.kind === 'ok' ? '' : 'warn'}`}>{banner.text}</div>}

      <div className="prefs-form">
        <label className="field">
          <span>Mobile phone</span>
          <input
            type="tel"
            placeholder="502-555-1234"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onBlur={() => { if (!phone.trim()) setSmsOptIn(false); }}
          />
        </label>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={emailOptIn}
            onChange={(e) => setEmailOptIn(e.target.checked)}
          />
          <span>Email me club announcements and updates</span>
        </label>

        <label className={`checkbox${smsDisabled ? ' disabled' : ''}`}>
          <input
            type="checkbox"
            checked={smsOptIn && hasPhone}
            disabled={smsDisabled}
            onChange={(e) => setSmsOptIn(e.target.checked)}
          />
          <span>
            Text me club announcements (SMS) from Eagle Ridge Outdoor Club
            {smsDisabled && <em className="muted"> — add a phone number first</em>}
          </span>
        </label>

        <p className="muted fine-print">
          By checking &ldquo;Text me,&rdquo; you agree to receive recurring automated
          text messages (club announcements and updates) from Eagle Ridge Outdoor
          Club at the number provided. Consent is not a condition of membership.
          Message frequency varies. Message and data rates may apply. Reply STOP to
          opt out, HELP for help. See our{' '}
          <a href="/terms">Terms of Service</a> and{' '}
          <a href="/privacy">Privacy Policy</a>.
        </p>

        <button disabled={saving} onClick={handleSave}>
          {saving ? 'Saving…' : 'Save preferences'}
        </button>
      </div>
    </div>
  );
}
